import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogActions, DialogTitle } from '@material-ui/core';

import { useErrorHandler } from 'src/hooks';
import {
  useCanCustomerCreateResource,
  useCustomFeatureLimitsData,
  useCustomerActions,
  useNotificationActions,
  usePerformanceActions,
  useTalkingPointsViewsActions,
  useWorkspaceActions,
} from 'src/store';
import { useUrlResources } from 'src/services';
import { FEATURE_LIMITS } from 'src/data/featureLimits';
import {
  RESOURCE_CONTENT_TYPE,
  RESOURCE_TAG,
  getCreatedResourceMessage,
} from 'src/data/performance';
import { useUpgradePopup } from 'src/services/popup';
import { CustomTypography, TYPOGRAPHY_VARIANT } from 'src/components/CustomTypography';
import isURL from 'validator/lib/isURL';
import { Button } from 'src/components/Button';
import { Select } from './components/Select';
import { Form } from './components/Form';
import { POPUP_STEP } from './const';
import { useViewState } from './useViewState';
import { useStyles } from './useStyles';

export const CreateTalkingPointsViewPopup = ({ open, onClose }) => {
  const styles = useStyles();

  const customerActions = useCustomerActions();
  const performanceActions = usePerformanceActions();
  const talkingPointsViewsActions = useTalkingPointsViewsActions();
  const notificationActions = useNotificationActions();
  const handleError = useErrorHandler();
  const canCustomerCreateResource = useCanCustomerCreateResource();
  const openUpgradePopup = useUpgradePopup();
  const urlResources = useUrlResources();
  const limitData = useCustomFeatureLimitsData(FEATURE_LIMITS.resourcesPerCustomer);
  const availableResources = limitData?.limit - limitData?.currentCount;
  const workspaceActions = useWorkspaceActions();

  const [url, setUrl] = useState('');
  const [error, setError] = useState('');
  const [resource, setResource] = useState(null);
  const [step, setStep] = useState(POPUP_STEP.select);
  const [type, setType] = useState(null);

  const { isExtractingTalkingPointsView, isCreatingResource } = useViewState();

  const handleConfirm = useCallback(async () => {
    if (error || (!resource && !url.length)) {
      return;
    }

    try {
      let resourceId = resource?.id;
      if (!resource) {
        if (!(await canCustomerCreateResource([url]))) {
          await openUpgradePopup(FEATURE_LIMITS.resourcesPerCustomer);
          return;
        }
        const createdResource = await performanceActions.extractResource({
          url,
          tags: [RESOURCE_TAG.general],
          contentType: RESOURCE_CONTENT_TYPE.url,
        });
        customerActions.refreshCurrentCustomerFeatureLimit();
        if (createdResource) {
          const message = getCreatedResourceMessage(createdResource, availableResources);
          notificationActions.displaySuccess(message, null, { timeout: 5000 });
        }
        resourceId = createdResource.id;
      }
      const createdTalkingPointsView = await talkingPointsViewsActions.extractTalkingPointsView(
        resourceId,
        type
      );
      await workspaceActions.refreshCurrentWorkspace();
      onClose(createdTalkingPointsView);
    } catch (error) {
      handleError(error);
    }
  }, [
    error,
    customerActions,
    notificationActions,
    talkingPointsViewsActions,
    resource,
    onClose,
    handleError,
    type,
    performanceActions,
    url,
    openUpgradePopup,
    canCustomerCreateResource,
    availableResources,
    workspaceActions,
  ]);

  const handleUrlInputChange = useCallback(
    (event, value) => {
      if (event) {
        setError('');
        setUrl(value);
      }
    },
    [setError, setUrl]
  );

  const handleUrlChange = useCallback(
    (event, value) => {
      if (value) {
        setUrl(value.content ? value.content : value);
        if (value.content) {
          setResource(value);
        }
      }
    },
    [setUrl, setResource]
  );

  const handleTypeSelect = useCallback(
    (event) => {
      setStep(POPUP_STEP.form);
      setType(event.target.value);
    },
    [setStep, setType]
  );

  const handleBack = useCallback(() => {
    setStep(POPUP_STEP.select);
  }, [setStep]);

  return (
    <Dialog
      open={open}
      classes={{ paper: styles.root }}
      onClose={() => (isExtractingTalkingPointsView ? null : onClose())}
      maxWidth="md"
      data-testid="CreateTalkingPointsViewPopup"
    >
      <DialogTitle>
        <CustomTypography variant={TYPOGRAPHY_VARIANT.semiBold16}>
          New talking points view
        </CustomTypography>
      </DialogTitle>
      {step === 'select' && <Select onTypeSelect={handleTypeSelect} />}

      {step === POPUP_STEP.form && (
        <>
          <Form
            onInputUrlChange={handleUrlInputChange}
            onUrlChange={handleUrlChange}
            urlResources={urlResources}
            url={url}
            error={error}
            isExtractingTalkingPointsView={isExtractingTalkingPointsView}
            type={type}
          />
          <DialogActions>
            <Button
              disabled={isExtractingTalkingPointsView}
              onClick={handleBack}
              variant="text"
              color="primary"
              aria-label="Cancel"
              data-testid="CreateTalkingPointsPopupCancelButton"
            >
              Back
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={!resource && !isURL(url)}
              loading={isExtractingTalkingPointsView || isCreatingResource}
              variant="contained"
              color="primary"
              aria-label="Confirm"
              data-testid="CreateTalkingPointsPopupCTAButton"
            >
              Next
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

CreateTalkingPointsViewPopup.propTypes = {
  className: PropTypes.string,
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
};
CreateTalkingPointsViewPopup.defaultProps = {
  className: null,
  open: false,
};
